import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SERVICE_CATEGORY_LABELS, URGENT_SERVICE_CATEGORIES,
  type ServiceCategory, type ServiceTypeRow, type ServiceTypeDetail, type ServiceRegionRow,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { Icon } from '../components/icons';

interface TemplateOption {
  id: string; name: string; latestVersionNo: number; fieldCount: number;
}

interface RegionForm {
  regionId: string;
  regionName: string;
  isProvince: boolean;
  enabled: boolean;
  contactPhone: string;
}

interface TypeForm {
  code: string;
  name: string;
  category: ServiceCategory;
  description: string;
  icon: string;
  templateId: string;
  isActive: boolean;
  sortOrder: string;
  regions: RegionForm[];
}

const CATEGORIES = Object.keys(SERVICE_CATEGORY_LABELS) as ServiceCategory[];

function emptyForm(regions: ServiceRegionRow[]): TypeForm {
  return {
    code: '',
    name: '',
    category: 'ambulance',
    description: '',
    icon: '',
    templateId: '',
    isActive: false,
    sortOrder: '0',
    regions: regions.map((r) => ({
      regionId: r.id,
      regionName: r.name,
      isProvince: r.isProvince,
      enabled: false,
      contactPhone: '',
    })),
  };
}

/**
 * Jenis Layanan — the service lines PMI offers, and where.
 *
 * The QUESTIONS are not edited here. This screen attaches a form template built in Form
 * Asesmen, which already handles all eight field types, version history, and preview. A
 * second field editor bolted onto this page would be a worse copy of one PMI has already
 * learned, and it would drift from it.
 */
export function ServiceTypes() {
  const { can } = useAuth();
  const writable = can('service:write');
  const list = usePagedList<ServiceTypeRow>('/services/types', {
    params: { includeInactive: 'true' },
  });

  const [regions, setRegions] = useState<ServiceRegionRow[]>([]);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceTypeDetail | null>(null);
  const [form, setForm] = useState<TypeForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, t] = await Promise.all([
          api.get<ServiceRegionRow[]>('/services/regions'),
          api.get<TemplateOption[]>('/services/form-templates'),
        ]);
        if (cancelled) return;
        setRegions(r);
        setTemplates(t);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : 'Gagal memuat wilayah dan form.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm(regions));
    setFormError(null);
    setOpen(true);
  }

  async function openEdit(row: ServiceTypeRow) {
    setFormError(null);
    try {
      const detail = await api.get<ServiceTypeDetail>(`/services/types/${row.id}`);
      setEditing(detail);
      setForm({
        code: detail.code,
        name: detail.name,
        category: detail.category,
        description: detail.description ?? '',
        icon: detail.icon ?? '',
        templateId: detail.templateId ?? '',
        isActive: detail.isActive,
        sortOrder: String(detail.sortOrder),
        regions: regions.map((r) => {
          const on = detail.regions.find((x) => x.regionId === r.id);
          return {
            regionId: r.id,
            regionName: r.name,
            isProvince: r.isProvince,
            enabled: on?.isActive ?? false,
            contactPhone: on?.contactPhone ?? '',
          };
        }),
      });
      setOpen(true);
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal memuat jenis layanan.');
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setFormError(null);

    const payload = {
      name: form.name,
      category: form.category,
      description: form.description || null,
      icon: form.icon || null,
      templateId: form.templateId || null,
      isActive: form.isActive,
      sortOrder: Number(form.sortOrder) || 0,
      // Only the ticked regions are sent. An untouched region is genuinely absent
      // rather than present-and-off, which is what makes the microsite catalogue a
      // simple join.
      regions: form.regions.filter((r) => r.enabled).map((r) => ({
        regionId: r.regionId,
        contactPhone: r.contactPhone || null,
        isActive: true,
      })),
    };

    try {
      if (editing) {
        await api.patch(`/services/types/${editing.id}`, payload);
        setNotice(`${form.name} disimpan.`);
        list.reload();
      } else {
        await api.post('/services/types', { ...payload, code: form.code });
        setNotice(`${form.name} ditambahkan.`);
        list.refresh();
      }
      setOpen(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Gagal menyimpan jenis layanan.');
    } finally {
      setSaving(false);
    }
  }

  const activeRegions = form?.regions.filter((r) => r.enabled).length ?? 0;
  const missingPhones = form && URGENT_SERVICE_CATEGORIES.includes(form.category)
    ? form.regions.filter((r) => r.enabled && !r.contactPhone.trim()).map((r) => r.regionName)
    : [];

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Jenis Layanan</h2>
        <p>
          {writable
            ? 'Pertanyaan tiap layanan diatur di Form Layanan. Di sini Anda memilih formnya dan wilayah yang melayaninya.'
            : 'Anda hanya bisa melihat daftar layanan.'}
        </p>
      </div>

      {notice && <div className="msg ok">{notice}</div>}
      {loadError && <div className="msg error">{loadError}</div>}

      <DataTable
        list={list}
        searchPlaceholder="Cari nama atau kode layanan…"
        action={writable ? { label: 'Tambah jenis layanan', onClick: openCreate } : undefined}
        empty="Belum ada jenis layanan."
        columns={[
          'Layanan', 'Kategori', 'Formulir', 'Wilayah',
          { label: 'Permintaan', align: 'right' }, 'Status', { label: '', width: '90px' },
        ]}
      >
        {list.items.map((row) => (
          <tr key={row.id}>
            <td>
              <div className="svc-name">
                {URGENT_SERVICE_CATEGORIES.includes(row.category) && (
                  <span className="urgency-stripe" aria-hidden="true" />
                )}
                <div>
                  <strong>{row.name}</strong>
                  <div className="mono muted" style={{ fontSize: 11 }}>{row.code}</div>
                </div>
              </div>
            </td>
            <td><span className="pill">{SERVICE_CATEGORY_LABELS[row.category]}</span></td>
            <td>
              {row.templateName ? (
                <>
                  {row.templateName}
                  <div className="muted" style={{ fontSize: 11.5 }}>
                    v{row.latestVersionNo ?? 1} · {row.fieldCount} pertanyaan
                  </div>
                </>
              ) : (
                <span className="muted">Belum ada form</span>
              )}
            </td>
            <td>
              {row.activeRegionCount === 0
                ? <span className="muted">Belum ada</span>
                : `${row.activeRegionCount} wilayah`}
            </td>
            <td style={{ textAlign: 'right' }} className="mono">{row.requestCount}</td>
            <td>
              <span className={row.isActive ? 'pill ok' : 'pill'}>
                {row.isActive ? 'Aktif' : 'Draf'}
              </span>
            </td>
            <td>
              {writable && (
                <button type="button" className="ghost" onClick={() => openEdit(row)}>
                  Ubah
                </button>
              )}
            </td>
          </tr>
        ))}
      </DataTable>

      {/* Only for someone who can act on it: a kota admin cannot open Form Layanan, and a
          link that bounces straight back reads as a broken page. */}
      {writable && (
        <p className="muted" style={{ fontSize: 13 }}>
          Menambah atau mengubah pertanyaan dilakukan di <Link to="/service-forms">Form Layanan</Link>.
          Setiap perubahan membuat versi baru, dan jawaban lama tetap terbaca pada versinya.
        </p>
      )}

      <Modal
        open={open}
        size="wide"
        title={editing ? `Ubah ${editing.name}` : 'Tambah jenis layanan'}
        description={
          'Kategori menentukan cara permintaan ditangani dan istilah statusnya. '
          + 'Form menentukan apa yang ditanyakan.'
        }
        onClose={() => setOpen(false)}
        onSubmit={save}
        footer={
          <>
            <button type="button" className="ghost" onClick={() => setOpen(false)}>Batal</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Menyimpan…' : 'Simpan'}
            </button>
          </>
        }
      >
        {form && (
          <div className="stack" style={{ gap: '1rem' }}>
            {formError && <div className="msg error">{formError}</div>}

            <div className="grid-2">
              <label className="field">
                <span className="label">Kode</span>
                <input
                  value={form.code}
                  disabled={!!editing}
                  required
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="AMB-JNZ"
                />
                {editing && (
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    Kode tidak bisa diubah: sudah dipakai di nomor permintaan yang terbit.
                  </span>
                )}
              </label>

              <label className="field">
                <span className="label">Nama</span>
                <input
                  value={form.name}
                  required
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Mobil Jenazah"
                />
              </label>

              <label className="field">
                <span className="label">Kategori</span>
                <select
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value as ServiceCategory })}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{SERVICE_CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
                <span className="muted" style={{ fontSize: 11.5 }}>
                  {URGENT_SERVICE_CATEGORIES.includes(form.category)
                    ? 'Kategori mendesak: tampil paling atas di microsite, dengan nomor telepon di atas formulir.'
                    : 'Kategori tidak mendesak: tampil setelah layanan mendesak.'}
                </span>
              </label>

              <label className="field">
                <span className="label">Urutan tampil</span>
                <input
                  type="number" min={0} max={999}
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                />
              </label>
            </div>

            <label className="field">
              <span className="label">Formulir pertanyaan</span>
              <select
                value={form.templateId}
                onChange={(e) => setForm({ ...form, templateId: e.target.value })}
              >
                <option value="">— belum dipilih —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (v{t.latestVersionNo}, {t.fieldCount} pertanyaan)
                  </option>
                ))}
              </select>
              <span className="muted" style={{ fontSize: 11.5 }}>
                Belum ada yang cocok? Buat formulirnya dulu di Form Asesmen.
              </span>
            </label>

            <label className="field">
              <span className="label">Keterangan singkat</span>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Penjemputan dan pengantaran jenazah"
              />
              <span className="muted" style={{ fontSize: 11.5 }}>
                Ini yang dibaca pemohon di bawah nama layanan.
              </span>
            </label>

            <div className="field">
              <span className="label">Wilayah yang melayani ({activeRegions})</span>
              <div className="region-grid">
                {form.regions.map((r, i) => (
                  <div key={r.regionId} className={r.enabled ? 'region-row is-on' : 'region-row'}>
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={r.enabled}
                        onChange={(e) => {
                          const next = [...form.regions];
                          next[i] = { ...r, enabled: e.target.checked };
                          setForm({ ...form, regions: next });
                        }}
                      />
                      <span>
                        {r.regionName}
                        {r.isProvince && <span className="pill" style={{ marginLeft: 6 }}>Provinsi</span>}
                      </span>
                    </label>
                    <input
                      value={r.contactPhone}
                      disabled={!r.enabled}
                      placeholder="Nomor telepon wilayah ini"
                      onChange={(e) => {
                        const next = [...form.regions];
                        next[i] = { ...r, contactPhone: e.target.value };
                        setForm({ ...form, regions: next });
                      }}
                    />
                  </div>
                ))}
              </div>
              <span className="muted" style={{ fontSize: 11.5 }}>
                Nomor ini yang muncul di microsite untuk pemohon di wilayah tersebut.
                Kalau dikosongkan, nomor umum wilayah yang dipakai.
              </span>
              {/*
                An urgent line with no number anywhere is the one misconfiguration that
                actually hurts somebody: the microsite then shows a requester who needs
                an ambulance now nothing but a form. Say so here, where it can be fixed.
              */}
              {missingPhones.length > 0 && (
                <div className="msg error" style={{ marginTop: '0.5rem' }}>
                  Layanan mendesak tapi belum ada nomor telepon untuk{' '}
                  {missingPhones.join(', ')}. Pemohon di wilayah itu hanya melihat
                  formulir, padahal untuk ambulans telepon jauh lebih cepat.
                </div>
              )}
            </div>

            <label className="check">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              <span>
                Aktif — tampil di microsite
                {form.isActive && !form.templateId && (
                  <span className="muted" style={{ display: 'block', fontSize: 11.5 }}>
                    Layanan aktif wajib punya formulir dengan pertanyaan.
                  </span>
                )}
              </span>
            </label>

            {editing && editing.requestCount > 0 && (
              <div className="msg">
                <Icon name="clipboard" size={15} />{' '}
                Sudah ada {editing.requestCount} permintaan lewat layanan ini, jadi layanan
                ini tidak bisa dihapus. Nonaktifkan saja kalau mau menghentikannya:
                riwayatnya tetap terbaca.
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
